import React from "react"
import { Box, Flex, Grid, GridItem, Text, useBoolean, useMediaQuery } from "@chakra-ui/react"
import { Colors } from "../../theme/colors"
import { ChevronDownIcon, ChevronRightIcon, InfoOutlineIcon } from "@chakra-ui/icons"
import { mobileBreakpoint } from "../../common/commonConstants"
import { SnapPopoverNotes } from "./popover"

interface ExpandableTableProps {
  defaultExpandRows?: boolean
  columnTemplate?: string
  isExpanded?: boolean
  onToggle?: () => void
  data: {
    hideHeading: boolean
    headings: string[]
    expandableTitle: string
    tableData: string[][] | React.ReactElement[][][]
    caption?: string
    applicationPrefix?: string | null
  }
}
export const ChevronDownIconComponent = ({ color }: { color?: string }): React.JSX.Element => (
  <Box mr="2" ml="1">
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
      <path d="M0.5 0.5L7 7L13.5 0.5" stroke={color ?? "#231F20"} />
    </svg>
  </Box>
)
export const ChevronRightIconComponent = ({ color }: { color?: string }): React.JSX.Element => (
  <Box mr="2" ml="1" transform={"rotate(-90deg)"}>
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none" transform="rotate(180)">
      <path d="M0.5 0.5L7 7L13.5 0.5" stroke={color ?? "#231F20"} />
    </svg>
  </Box>
)

export const ExpandableTableComponent = ({
  data: { headings, expandableTitle, tableData, caption, applicationPrefix = "", hideHeading },
  defaultExpandRows = false,
  columnTemplate,
  isExpanded: controlledExpand,
  onToggle
}: ExpandableTableProps): React.JSX.Element => {
  const [internalExpand, setInternalExpand] = useBoolean(false)
  const expand = controlledExpand !== undefined ? controlledExpand : internalExpand
  const handleToggle = (): void => {
    if (onToggle !== undefined) { onToggle() } else { setInternalExpand.toggle() }
  }

  const getMinHeight = (headings: string[], hideHeading: boolean): string => (hideHeading ? "42px" : "84px")
  const [isDesktop] = useMediaQuery(`(min-width: ${mobileBreakpoint}px)`)
  const [isTouchDevice] = useMediaQuery("(hover: none)")

  return (
    <Box mt={hideHeading ? "1px" : "16px"}>
      <Grid
        templateColumns={columnTemplate ?? `repeat(${headings.length}, 1fr)`}
        gridTemplateRows={hideHeading ? `0 repeat(${tableData.length}, minMax(42px, auto))` : `repeat(${tableData.length + 2}, minMax(42px, auto))`}
        overflow={expand ? "auto" : "hidden"}
        maxHeight={"3000px"}
        h={expand ? "auto" : getMinHeight(headings, hideHeading)}
        transition={"0.5s"}
        justifyContent={"flex-start"}
      >
        <>
          {headings.map((heading, i) => (
            <GridItem
              key={`${heading}-${i}gridItem`}
              bg={hideHeading ? "blue" : Colors.Primary.accent}
              borderRight={i < headings.length - 1 ? "1px solid white" : "none"}
              {...(i === 0 && { minW: ["auto", "auto", "300px"] })}
              color="white"
              textTransform={"capitalize"}
              h={hideHeading ? "40px" : "auto"}
              visibility={hideHeading ? "hidden" : "visible"}
            >
              <Flex py="2" align="center" height="100%" fontWeight={"bold"} justify={i === 0 ? "flex-start" : "center"} px={["8px", "8px", "21px"]}>
                <Text variant={["RCaptionBold", "RCaptionBold", "RBodyBold"]}>
                  {/* Adding application prefix if its not null */}
                  {i === 0 && applicationPrefix ? `${applicationPrefix} ` : ""}

                  {i !== 0 ? heading : ""}
                </Text>
              </Flex>
            </GridItem>
          ))}
        </>
        {/* Expandable Row */}
        <GridItem colSpan={headings.length} justifySelf={"normal"} bg={Colors.Secondary.c2_500} onClick={handleToggle} userSelect={"none"}>
          <Flex align="center" height="100%" px={["5px", "5px", "14px"]}>
            {(!isDesktop || isTouchDevice) && (
              <>
                {expand && <ChevronDownIconComponent />}
                {!expand && <ChevronRightIconComponent />}
              </>
            )}
            {isDesktop && !isTouchDevice && (
              <>
                {expand && <ChevronDownIcon width="24px" height="24px" />}
                {!expand && <ChevronRightIcon width="24px" height="24px" />}
              </>
            )}

            <Text variant={["RCaption", "RCaption", "RBody"]} ml={[0, 0, "2px"]}>
              {expandableTitle?.split("|")[0] ?? ""}
              <SnapPopoverNotes
                key={expandableTitle?.split("|")[1] ?? ""}
                noteIcon={<InfoOutlineIcon />}
                text={
                  expandableTitle?.split("|")[1]
                    ? `The maximum FRL of this Element/Barrier is ${expandableTitle?.split(
                      "|"
                    )[1]} '. The FRL required on a project may be less than this maximum FRL'`
                    : ""
                }
              />
            </Text>
            {caption && <Text variant={"RCaption"}>{caption}</Text>}
          </Flex>
        </GridItem>

        {tableData.map((row, rowIndex) => (
          <React.Fragment key={`grid-${rowIndex}-gridrow-react-fragment`}>
            {row.map((cellValue, cellIndex) => (
              <GridItem
                key={`grid-${rowIndex}${cellIndex}-grid-row`}
                {...(cellIndex === 0 && { width: ["180px", "100%", "100%"], pl: [0, 0, "30px"] })}
                borderBottom={`1px solid ${Colors.Secondary.c2_300}`}
              >
                <Flex p="2" {...(cellIndex !== 0 && { justify: "center" })} height="100%" flexWrap={"wrap"} align={"center"}>
                  {React.isValidElement(cellValue) ? (
                    <Box px={[0, 0, "30px"]}>
                      {cellValue}
                    </Box>
                  ) : (
                    <Text variant={["RCaption", "RCaption", "RBody"]} px={[0, 0, "30px"]}>
                      {cellValue}
                    </Text>
                  )}
                </Flex>
              </GridItem>
            ))}
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  )
}
